import React, { useRef, useState } from "react"
import styled from "@emotion/styled"
import Slider from "react-slick"

import Section from "src/components/Section"
import Grid from "src/components/Grid"
import Media from "src/components/Media"
import TextLockup from "src/components/TextLockup"
import { typography, animations, mq, globals } from "src/styles"

const Wrapper = styled(Section)`
  position: relative;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 0px !important;
  ${({ isFirstSection }) => (isFirstSection ? "" : "")}
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
  `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => hideMobile && "display: none"}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => hideDesktop && "display: none"}
  }
`

const StyledGrid = styled(Grid)`
  max-width: 1536px;
  margin: 0 auto;
`

const ColumnWrapper = styled.div`
  ${globals.verticalSpacing("padding-top", 1)}
  ${globals.verticalSpacing("padding-bottom", 1)}
  z-index: 1;
  ${mq.mediumAndUp} {
    height: unset;
    /* padding-top: unset; */
  }
  ${({ mediaCol }) =>
    mediaCol
      ? `
    .gatsby-image-wrapper,
    .video-wrapper {
      width: 100%;
      height: 100%;
    }
    ${mq.largerAndUp} {
      height: 100%;
      position: relative;
      .gatsby-image-wrapper,
      .video-wrapper {
        position: absolute !important;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  `
      : ``}
  ${mq.largerAndUp} {
    h1,
    h2 {
      max-width: 37.5rem;
    }
    h3 {
      max-width: 37.5rem;
    }
    h4,
    h5 {
      max-width: 37.5rem;
    }
    h6 {
      max-width: 37.5rem;
    }
    p {
      max-width: 32rem;
    }
  }
`

const TextWrapper = styled.div`
  padding-left: 20px;
  padding-right: 20px;
  ${mq.extraLargeAndUp} {
    padding-left: 0;
    padding-right: 0;
  }
  ${mq.largerAndUp} {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const Line = styled.svg`
  position: absolute;
  width: 90vw;
  height: auto;
  right: 0;
  margin: 0 auto;
  bottom: -10px;

  ${mq.largerAndUp} {
    bottom: unset;
    width: 50vw;
    top: 30%;
  }
`

const ImageWrapper = styled.div`
  height: 100%;
  position: relative;
  align-self: center;
  aspect-ratio: 1.5;
  ${mq.largerAndUp} {
    aspect-ratio: unset;
  }
  .gatsby-image-wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    bottom: 0;
    left: 0;
  }
  .gatsby-image-wrapper img {
    object-fit: contain !important;
  }

  ${mq.largerAndUp} {
    > div {
      overflow: hidden;
    }
  }
  ${mq.largeAndBelow} {
    .gatsby-image-wrapper,
    .video-wrapper {
      overflow: hidden;
    }
    ${mq.largeAndBelow} {
      ${({ imagePositionMobile }) => imagePositionMobile && "order: -1"}
    }
  }
  text-align: left;
  h3,
  p {
    transition: color ${animations.mediumSpeed} ease-in-out;
    margin: 0;
  }
  h3 {
    ${typography.articleTitleSmall}
    max-width: 24em;
    margin-bottom: 0.25em;
    margin-top: -0.1em;
    margin-top: 0.8em;
  }
  p {
    ${typography.bodySmall}
    max-width: 26em;
    margin-bottom: 0.1em;
  }
`

// const ImageSlider = styled(Slider)`
//   z-index: 0;
//   ${mq.largerAndUp} {
//     padding-bottom: 40px;
//     padding-bottom: 0;
//     margin: -1px -1px;
//     .slide-content {
//       margin: -1px 0;
//     }
//   }
// `

// const Quote = styled.div`
//   position: absolute;
//   z-index: 1;
//   top: 35%;
//   right: 5%;
//   width: 100%;
//   max-width: 20%;
// `

// const Slide = styled.div`
//   position: relative;
//   p:nth-child(2) {
//     font-weight: bold;
//   }
// `

const PageIntroSlider = ({
  theme,
  spacing,
  prevTheme,
  nextTheme,
  eyebrow,
  headingSize,
  headline,
  hideMobile,
  hideDesktop,

  _rawText,
  actions,
  slides,
  textAlignment = "left",
  isFirstSection,
  listType,
  id,
  imagePositionMobile,
}) => {
  const slider = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)

  const settings = {
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipe: true,
    speed: 500,
    dots: false,
    beforeChange: (_, newIndex) => setCurrentSlide(newIndex),
  }

  // set responsive image sizes
  const sizes = "(min-width: " + mq.mediumBreakpoint + "px) 50vw, 100vw"

  return (
    <Wrapper
      setTheme={theme}
      prevTheme={prevTheme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      fullWidth
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      <Line
        width="657"
        height="399"
        viewBox="0 0 657 399"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 230C2 104.079 104.079 2 230 2H685V397H2V230Z"
          stroke="url(#paint0_linear_121_8243)"
          strokeWidth="4px"
        />
        <defs>
          <linearGradient
            id="paint0_linear_121_8243"
            x1="14.7969"
            y1="163.274"
            x2="687"
            y2="165.463"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.0700903" stopColor="#3D6EDF" />
            <stop offset="0.948084" stopColor="#49E8AB" />
          </linearGradient>
        </defs>
      </Line>
      <StyledGrid small="[1]" large="[6] [6]" columnGap={true} vAlign="center">
        <ColumnWrapper>
          <TextWrapper>
            <TextLockup
              entranceDelay={1}
              eyebrow={eyebrow}
              text={_rawText}
              textSize={"bodyMedium"}
              headingSize={headingSize ? headingSize : "h3"}
              headline={headline}
              headlineElement={"h1"}
              actions={actions}
              theme={theme}
              listType={listType}
              alignment={textAlignment}
              transitionIn={false}
            />
          </TextWrapper>
        </ColumnWrapper>
        <ImageWrapper imagePositionMobile={imagePositionMobile}>
          <Media
            sizes={sizes}
            media={slides[0].media}
            altText={slides[0].title}
            ratio={0.75}
          />
        </ImageWrapper>
      </StyledGrid>
    </Wrapper>
  )
}

export default PageIntroSlider
